import React, { useEffect, useRef, useState } from "react";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import { Row, Col, Divider } from "antd";
import { StepperTypeAttributes } from "../../enums/StepperTypeAttributes";
import tickIcon from "../../assets/images/tick-icon-new.svg";
import tickIconActive from "../../assets/images/current-active-step.svg";
import styles from "./TNStepperForm.module.scss";
import clsx from "clsx";
interface Step {
  title: string;
  activeIcon: string;
  inactiveIcon: string;
}

interface StepperInterfaceProps {
  type: StepperTypeAttributes;
  currentStep: number;
  handleCurrentStep: (step: number) => void;
  handleSubmit?: () => void;
  currentFormStep: React.ReactNode;
  steps: Step[];
  className?: string;
  showPricingDetailsView: boolean;
  isView?: boolean;
}
const TNStepperForm = ({
  type,
  currentStep,
  handleCurrentStep,
  currentFormStep,
  steps,
  className,
  showPricingDetailsView,
  isView,
}: StepperInterfaceProps) => {
  const { STEPPER_STEPS, NAVIGATION_STEPS } = StepperTypeAttributes;
  const [animateStep, setAnimateStep] = useState<number | null>(null);
  const prevStepRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevStepRef.current !== null && prevStepRef.current < currentStep) {
      setAnimateStep(prevStepRef.current);
    }
    prevStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    if (animateStep !== null) {
      const timer = setTimeout(() => {
        setAnimateStep(null);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [animateStep]);

  const getStepIcon = (stepIndex: number, currentStep: number) => {
    if (stepIndex < currentStep) {
      return tickIcon;
    } else if (stepIndex === currentStep) {
      return steps[stepIndex - 1].activeIcon;
    } else {
      return steps[stepIndex - 1].inactiveIcon;
    }
  };

  return (
    <div className={styles.StepperHeader}>
      <ConditionalRender visible={type === STEPPER_STEPS}>
        <div
          className={clsx({
            className: !showPricingDetailsView && className,
            [styles.StepperHeaderContainer]:
              !showPricingDetailsView && !className,
            [styles.hiddenStepperInterface]:
              showPricingDetailsView && !className,
          })}
        >
          <Row gutter={10} justify="space-between">
            {steps.map((step, index) => {
              const stepIndex = index + 1;
              return (
                <>
                  <Col
                    key={index}
                    xs={24 / steps.length}
                    sm={24 / steps.length}
                    md={24 / steps.length}
                    lg={24 / steps.length}
                    xl={24 / steps.length}
                  >
                    <div
                      className={clsx(styles.StepperHeaderItem, {
                        [styles.notAllowed]: stepIndex > currentStep,
                      })}
                      onClick={() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        isView ? handleCurrentStep(stepIndex) : null;
                      }}
                    >
                      <img
                        src={getStepIcon(stepIndex, currentStep)}
                        alt={step.title}
                        className={clsx(styles.StepIcon, {
                          [styles.animate]:
                            animateStep === stepIndex &&
                            getStepIcon(stepIndex, currentStep) ===
                              tickIconActive,
                        })}
                      />
                      <p
                        className={clsx(styles.StepperItemText, {
                          [styles.CompletedStepText]: stepIndex < currentStep,
                          [styles.ActiveStepText]: stepIndex === currentStep,
                          [styles.NonActiveStepText]: stepIndex > currentStep,
                        })}
                      >
                        {step.title}
                      </p>
                    </div>
                  </Col>
                  {index < steps.length - 1 && (
                    <Col flex="auto">
                      <Divider className={styles.StepperDivider} />
                    </Col>
                  )}
                </>
              );
            })}
          </Row>
        </div>
        <div className={styles.StepperContentContainer}>{currentFormStep}</div>
      </ConditionalRender>
      <ConditionalRender visible={type === NAVIGATION_STEPS}>
        <div
          className={clsx({
            className: !showPricingDetailsView && className,
            [styles.TabNavigationHeaderContainer]:
              !showPricingDetailsView && !className,
            [styles.hiddenStepperInterface]:
              showPricingDetailsView && !className,
          })}
        >
          <Row gutter={10} justify="space-between">
            {steps.map((step, index) => (
              <Col
                key={index}
                xs={24 / steps.length}
                sm={24 / steps.length}
                md={24 / steps.length}
                lg={24 / steps.length}
                xl={24 / steps.length}
              >
                <div
                  className={clsx(styles.TabHeaderItem, {
                    [styles.activeTab]: index + 1 === currentStep,
                  })}
                  onClick={() => handleCurrentStep(index + 1)}
                >
                  <div
                    className={clsx(styles.TabTitle, {
                      [styles.activeTabTitle]: index + 1 === currentStep,
                    })}
                  >
                    {step.title}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
        <div className={styles.TabContentContainer}>{currentFormStep}</div>
      </ConditionalRender>
    </div>
  );
};

export default TNStepperForm;
